import {
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  Grid,
  TextField,
  FormControl,
  FormHelperText,
  Tooltip,
} from "@mui/material";
import { Alert } from "@mui/material";
import ContextOrganizationField, {
  ContextOrganizationFieldMode,
} from "components/ContextOrganizationField";
import SelectEntity from "components/SelectEntity";
import useGlobalContext from "hooks/globalData";
import { pick } from "lodash";
import React, { useMemo } from "react";
import { Field, FormProps, withTypes } from "react-final-form";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";
import {
  IMenu,
  ListRequestOfBaseFilter,
  ListResponseOfAdminMenuType,
  MenuRequest,
} from "services/KnowfullyAdminClient";
import { CommonFormProps } from "types/common-types";

const { Form } = withTypes<MenuRequest>();

const styles = {
  feedback: {
    width: "100%",
    margin: "20px 0 20px 0",
  },
};

const MenuForm = ({
  entity: menu,
  submitLabel,
  onCancel,
  onSubmit,
}: CommonFormProps<IMenu, MenuRequest> & FormProps<MenuRequest>) => {
  const { formatMessage: t } = useIntl();
  const { menuService } = useGlobalContext();

  const init = useMemo(
    () => ({
      ...pick(menu ?? {}, [
        "organizationId",
        "menuTypeId",
        "menuType",
        "name",
        "description",
      ]),
    }),
    [menu]
  );

  const fetchedMenuTypes = useQuery<ListResponseOfAdminMenuType>(
    ["menuService.getMenuTypes"],
    () =>
      menuService.getMenuTypes(
        new ListRequestOfBaseFilter({
          sortField: "name",
        })
      )
  );

  return useMemo(() => {
    const requiredField = (value: string | undefined) =>
      value ? undefined : "Required";
    return (
      <Form
        onSubmit={onSubmit}
        initialValues={init}
        render={({ handleSubmit, submitting, submitError, values, form }) => (
          <form onSubmit={handleSubmit}>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <ContextOrganizationField
                  values={values}
                  formChange={form.change}
                  mode={
                    menu
                      ? ContextOrganizationFieldMode.EditDisabled
                      : ContextOrganizationFieldMode.SetByHeader
                  }
                />

                <Field name="menuTypeId" required validate={requiredField}>
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <FormControl
                        fullWidth
                        error={meta.error && meta.submitFailed}
                      >
                        <Tooltip
                          title={
                            menu ? t({ id: "menuTypeCannotBeChanged" }) : ""
                          }
                        >
                          <span>
                            <SelectEntity
                              fetchedData={fetchedMenuTypes}
                              labelField="name"
                              label={t({ id: "menuFormMenuTypeLabel" })}
                              disabled={!!menu}
                              {...input}
                              textFieldProps={{
                                error: meta.error && meta.submitFailed,
                              }}
                            />
                          </span>
                        </Tooltip>
                        {meta.error && meta.submitFailed && (
                          <FormHelperText>{meta.error}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                  )}
                </Field>

                <Field name="name" required validate={requiredField}>
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <TextField
                        variant="standard"
                        fullWidth
                        label={t({ id: "nameLabel" })}
                        InputLabelProps={{ required: true }}
                        error={meta.error && meta.submitFailed}
                        helperText={
                          meta.error && meta.submitFailed && meta.error
                        }
                        {...input}
                        data-cy="nameLabelText"
                      />
                    </Grid>
                  )}
                </Field>
                <Field name="description">
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <TextField
                        variant="standard"
                        fullWidth
                        label={t({ id: "descriptionLabel" })}
                        error={meta.error && meta.submitFailed}
                        helperText={
                          meta.error && meta.submitFailed && meta.error
                        }
                        {...input}
                        data-cy="descriptionText"
                      />
                    </Grid>
                  )}
                </Field>

                {submitError && (
                  <Alert sx={styles.feedback} severity="error">
                    {submitError}
                  </Alert>
                )}
              </Grid>
            </DialogContent>

            <DialogActions>
              {submitting && <CircularProgress size={24} />}
              <Button
                disabled={submitting}
                onClick={onCancel}
                data-cy="cancelButton"
              >
                {t({ id: "cancelButton" })}
              </Button>

              <Button
                type="submit"
                color="primary"
                disabled={submitting}
                data-cy="saveButton"
              >
                {submitLabel}
              </Button>
            </DialogActions>
          </form>
        )}
      />
    );
  }, [fetchedMenuTypes, init, menu, onCancel, onSubmit, submitLabel, t]);
};

export default MenuForm;
